/**
 * Wizard step state + navigation. Owns which panel is visible, the stepper
 * active/done classes, and the footer buttons (Prev hidden on step 1; Next vs
 * Submit on the last step). Backward jumps are allowed only to visited steps.
 */

export type StepChangeHandler = (step: number) => void;

/** Must match the panel count rendered by templates/frontend/company-wizard.php. */
export const LAST_STEP = 3;

export class WizardState {
  currentStep = 1;
  readonly visited = new Set<number>([1]);
  private onChange: StepChangeHandler | null = null;

  constructor(private readonly root: HTMLElement) {}

  onStepChange(handler: StepChangeHandler): void {
    this.onChange = handler;
  }

  markDone(step: number): void {
    this.visited.add(step);
  }

  canVisit(step: number): boolean {
    return this.visited.has(step);
  }

  goTo(step: number): void {
    if (step < 1 || step > LAST_STEP) {
      return;
    }
    this.currentStep = step;
    this.visited.add(step);
    this.applyPanels();
    this.applyStepper();
    this.applyFooter();
    if (this.onChange) {
      this.onChange(step);
    }
  }

  private applyPanels(): void {
    const panels = this.root.querySelectorAll<HTMLElement>('.iew-step[data-step]');
    panels.forEach((panel) => {
      const n = Number(panel.dataset.step);
      const active = n === this.currentStep;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
    });
  }

  private applyStepper(): void {
    const steps = this.root.querySelectorAll<HTMLElement>('.iew-stepper__step[data-step]');
    steps.forEach((el) => {
      const n = Number(el.dataset.step);
      el.classList.toggle('is-active', n === this.currentStep);
      el.classList.toggle('is-done', n < this.currentStep || (this.visited.has(n) && n !== this.currentStep));
    });
  }

  private applyFooter(): void {
    const prev = this.root.querySelector<HTMLButtonElement>('[data-iew="prev"]');
    const next = this.root.querySelector<HTMLButtonElement>('[data-iew="next"]');
    const submit = this.root.querySelector<HTMLButtonElement>('[data-iew="submit"]');
    if (prev) {
      prev.hidden = this.currentStep === 1;
    }
    if (next) {
      next.hidden = this.currentStep === LAST_STEP;
    }
    if (submit) {
      submit.hidden = this.currentStep !== LAST_STEP;
    }
  }
}
